import { useState, useEffect, useCallback, useMemo } from 'react';
import compose from 'lodash/fp/compose';
import fromPairs from 'lodash/fp/fromPairs';
import toPairs from 'lodash/fp/toPairs';

const createURLSearchParams = (params) => new URLSearchParams(params);

const filterNonNull = (pairs) =>
  pairs.filter(([_, value]) => value !== null && value !== '');

const updateURLSearchParams = (newParams) => {
  const transform = compose(
    createURLSearchParams,
    fromPairs,
    filterNonNull,
    toPairs,
  );

  const newSearchString = transform(newParams).toString();
  const newUrl = `${window.location.pathname}?${newSearchString}`;
  window.history.pushState({}, '', newUrl);

  return createURLSearchParams(newSearchString);
};

export const useSearchParams = () => {
  const [searchParams, setSearchParams] = useState(() =>
    createURLSearchParams(window.location.search),
  );

  const setNewSearchParams = useCallback((newParamsOrFunction) => {
    try {
      setSearchParams((prevSearchParams) => {
        const prevParamsObject = Object.fromEntries(prevSearchParams.entries());
        const newParams =
          typeof newParamsOrFunction === 'function'
            ? newParamsOrFunction(prevParamsObject)
            : newParamsOrFunction;

        return updateURLSearchParams({ ...prevParamsObject, ...newParams });
      });
    } catch (error) {
      console.error('Failed to set new search params:', error.message);
    }
  }, []);

  const handleRouteChange = useCallback(() => {
    setSearchParams(createURLSearchParams(window.location.search));
  }, []);

  const urlChangeEvent = useMemo(() => new Event('urlchange'), []);

  useEffect(() => {
    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('urlchange', handleRouteChange);

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function () {
      originalPushState.apply(this, arguments);
      window.dispatchEvent(urlChangeEvent);
    };

    window.history.replaceState = function () {
      originalReplaceState.apply(this, arguments);
      window.dispatchEvent(urlChangeEvent);
    };

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('urlchange', handleRouteChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [handleRouteChange, urlChangeEvent]);

  return [searchParams, setNewSearchParams];
};
